import type { S25ItemI } from "../../../pojo/S25ItemI";
import { S25Util } from "../../../util/s25-util";
import type { EventForm } from "..";

export interface DateOccurrenceRow {
    occ: EventForm.Occurrence;
    locations: S25ItemI[];
    resources: S25ItemI[];
    setupDate: Date | null;
    preDate: Date | null;
    postDate: Date | null;
    takedownDate: Date | null;
    states: S25ItemI[];
}

/** Fields the date occurrences table can edit; used to cancel in-progress modal input. */
export interface DateOccurrenceEdits {
    evStartDt: Date;
    evEndDt: Date;
    comment: string;
    state: S25ItemI;
    includeInSearch: boolean;
    prevStartTime?: string;
    prevEndTime?: string;
    objOccIncludedByKey: Record<string, boolean>;
}

export function objOccIncludedKey(itemTypeId: number, itemId: number): string {
    return `${itemTypeId}-${itemId}`;
}

function objOccMatchesOccurrence(objOcc: EventForm.ObjectOccurrence, occurrence: EventForm.Occurrence): boolean {
    const linkedOcc = objOcc.occ;
    if (linkedOcc.uuid != null && String(linkedOcc.uuid) === String(occurrence.uuid)) {
        return true;
    }
    return linkedOcc.rsrvId != null && occurrence.rsrvId != null && linkedOcc.rsrvId === occurrence.rsrvId;
}

export function cloneDateOccurrence(occurrence: EventForm.Occurrence): EventForm.Occurrence {
    return {
        ...occurrence,
        evStartDt: new Date(occurrence.evStartDt),
        evEndDt: new Date(occurrence.evEndDt),
        state: occurrence.state ? { ...occurrence.state } : occurrence.state,
        locations: occurrence.locations?.map((item) => ({ ...item })),
        resources: occurrence.resources?.map((item) => ({ ...item })),
        minutes: occurrence.minutes ? { ...occurrence.minutes } : occurrence.minutes,
    };
}

export function captureDateOccurrenceEdits(
    formModel: EventForm,
    occurrence: EventForm.Occurrence,
): DateOccurrenceEdits {
    const objOccIncludedByKey: Record<string, boolean> = {};
    for (const objOcc of formModel.getObjOccs?.() ?? []) {
        if (!objOccMatchesOccurrence(objOcc, occurrence)) {
            continue;
        }
        const itemId = S25Util.parseInt(objOcc.item?.itemId);
        const itemTypeId = S25Util.parseInt(objOcc.item?.itemTypeId);
        if (!Number.isFinite(itemId) || !Number.isFinite(itemTypeId)) {
            continue;
        }
        objOccIncludedByKey[objOccIncludedKey(itemTypeId, itemId)] = !!objOcc.isIncluded;
    }

    return {
        evStartDt: new Date(occurrence.evStartDt),
        evEndDt: new Date(occurrence.evEndDt),
        comment: occurrence.comment ?? "",
        state: occurrence.state ? { ...occurrence.state } : { itemId: 1, itemName: "Active" },
        includeInSearch: !!occurrence.includeInSearch,
        prevStartTime: occurrence.prevStartTime,
        prevEndTime: occurrence.prevEndTime,
        objOccIncludedByKey,
    };
}

export function applyDateOccurrenceEdits(
    formModel: EventForm,
    occurrence: EventForm.Occurrence,
    edits: DateOccurrenceEdits,
): void {
    occurrence.evStartDt = new Date(edits.evStartDt);
    occurrence.evEndDt = new Date(edits.evEndDt);
    occurrence.comment = edits.comment;
    occurrence.state = edits.state ? { ...edits.state } : occurrence.state;
    occurrence.includeInSearch = edits.includeInSearch;
    occurrence.prevStartTime = edits.prevStartTime;
    occurrence.prevEndTime = edits.prevEndTime;

    for (const objOcc of formModel.getObjOccs?.() ?? []) {
        if (!objOccMatchesOccurrence(objOcc, occurrence)) {
            continue;
        }
        const itemId = S25Util.parseInt(objOcc.item?.itemId);
        const itemTypeId = S25Util.parseInt(objOcc.item?.itemTypeId);
        if (!Number.isFinite(itemId) || !Number.isFinite(itemTypeId)) {
            continue;
        }
        const key = objOccIncludedKey(itemTypeId, itemId);
        if (Object.prototype.hasOwnProperty.call(edits.objOccIncludedByKey, key)) {
            objOcc.isIncluded = edits.objOccIncludedByKey[key];
        }
    }
}
